/**
 * Google addresses attached to one login.
 *
 * Sign-in used to match on the account's own `email` column, which allows
 * exactly one address per user. Clinics do not work that way: a doctor and their
 * secretary share one login and each has their own Gmail, so whichever of them
 * did not own the column was simply locked out of Google sign-in — and an admin
 * creating an account on someone's behalf had no way to attach an address at
 * all.
 *
 * The same panel serves both cases. Passed no `userId` it manages the signed-in
 * account; passed one, an admin manages that account instead. The backend
 * enforces the distinction — a non-admin naming someone else's id gets a 403 —
 * so this component never has to be the security boundary.
 */

import { useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  SectionTitle,
  Spinner,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { googleAccountApi } from '../../api/endpoints';
import { isValidEmail } from '../../domain/validation';

/** Suggestions for the optional label, so the field is not a blank stare. */
const LABEL_SUGGESTIONS = ['الطبيب', 'السكرتارية', 'الاستقبال', 'المدير'];

export function GoogleAccounts({ userId = null, ownerName = null, embedded = false }) {
  const { success, error: toastError } = useNotifications();

  const state = useAsync(() => googleAccountApi.list(userId), [userId], { initialData: [] });
  const accounts = state.data || [];

  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState(null);
  const [removing, setRemoving] = useState(null);

  const handleAdd = async () => {
    const address = email.trim().toLowerCase();

    if (!isValidEmail(address)) {
      setFormError('أدخل بريداً إلكترونياً صحيحاً');
      return;
    }

    setAdding(true);
    setFormError(null);
    try {
      await googleAccountApi.add({
        email: address,
        label: label.trim() || null,
        ...(userId ? { userId } : {}),
      });
      setEmail('');
      setLabel('');
      success('تمت إضافة البريد');
      state.reload();
    } catch (err) {
      // 409 is the backend saying the address already opens some account —
      // worth showing in place rather than as a toast that scrolls away, since
      // it is a correctable mistake.
      if (err.status === 409) setFormError(err.message);
      else toastError(err.message || 'تعذر إضافة البريد');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      await googleAccountApi.remove(removing.id);
      success('تم حذف البريد');
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر حذف البريد');
    } finally {
      setRemoving(null);
    }
  };

  const body = (
    <>
      <p className="mb-4 text-sm leading-relaxed text-slate-400">
        {ownerName
          ? `عناوين Google التي يمكنها تسجيل الدخول إلى حساب ${ownerName}.`
          : 'يمكنك إضافة أكثر من بريد Google لتسجيل الدخول إلى نفس الحساب — مثلاً بريد الطبيب وبريد السكرتارية.'}{' '}
        كل بريد يخص حساباً واحداً فقط.
      </p>

      {/*
       * A failed list is not a failed panel.
       *
       * Reading the addresses and adding one are separate requests, and only
       * the read can fail on its own — so a full-width alarm here told the
       * doctor the whole feature was down while the form underneath worked
       * perfectly. One quiet line says the same thing without the panic, and
       * leaves the part that still works looking usable.
       */}
      {state.error ? (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] p-3">
          <Icon name="alert-circle" size={16} className="shrink-0 text-amber-400" />
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-amber-200/90">
            تعذر عرض العناوين المضافة حالياً. يمكنك إضافة بريد جديد من الأسفل كالمعتاد.
          </p>
          <Button variant="subtle" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            إعادة المحاولة
          </Button>
        </div>
      ) : state.loading && accounts.length === 0 ? (
        <Spinner variant="inline" label="جاري تحميل العناوين..." />
      ) : accounts.length === 0 ? (
        <EmptyState
          icon="mail"
          title="لا توجد عناوين مضافة"
          message="أضف بريد Google ليتمكن صاحبه من الدخول لهذا الحساب."
        />
      ) : (
        <ul className="mb-5 space-y-2">
          {accounts.map((account) => (
            <li
              key={account.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-3"
            >
              <span className="rounded-lg bg-red-500/10 p-2 text-red-300">
                <Icon name="mail" size={16} />
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white" dir="ltr">
                  {account.email}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {account.label && (
                    <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
                      {account.label}
                    </Badge>
                  )}
                  {/* Which addresses are actually in use is the question an
                      admin looking at a shared login is trying to answer. */}
                  <Badge
                    className={
                      account.lastUsedAt
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        : 'border-slate-600 bg-slate-800 text-slate-400'
                    }
                  >
                    {account.lastUsedAt ? 'تم الدخول به' : 'لم يُستخدم بعد'}
                  </Badge>
                </div>
              </div>

              <Button
                variant="subtle"
                icon="trash-2"
                className="!text-red-300"
                onClick={() => setRemoving(account)}
              >
                حذف
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3 rounded-xl border border-white/[0.07] bg-slate-900/50 p-4">
        <Field label="بريد Google" error={formError} required>
          <Input
            type="email"
            dir="ltr"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setFormError(null);
            }}
            placeholder="name@gmail.com"
          />
        </Field>

        <Field label="الوصف" hint="اختياري — لتعرف صاحب البريد لاحقاً">
          <Input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="السكرتارية"
          />
        </Field>

        <div className="flex flex-wrap gap-1.5">
          {LABEL_SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => setLabel(suggestion)}
              className="rounded-lg border border-white/10 bg-slate-800 px-2.5 py-1 text-[11px] font-bold text-slate-300 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
            >
              {suggestion}
            </button>
          ))}
        </div>

        <div className="flex justify-end">
          <Button icon="plus" loading={adding} disabled={!email.trim()} onClick={handleAdd}>
            إضافة البريد
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
        title="حذف البريد"
        confirmLabel="حذف"
        message={`لن يتمكن ${removing?.email || 'هذا البريد'} من تسجيل الدخول لهذا الحساب بعد الحذف.`}
      />
    </>
  );

  // Inside the admin's account modal the surrounding chrome is already a panel;
  // nesting another one boxes the section twice.
  if (embedded) return <div>{body}</div>;

  return (
    <Panel>
      <SectionTitle icon="mail">حسابات Google المرتبطة</SectionTitle>
      {body}
    </Panel>
  );
}

export default GoogleAccounts;
